<template>
  <section>
    <div class="gm-tabs" role="tablist" aria-label="Template collections">
      <button v-for="tab in collections" :key="tab.id" :class="{ active: collection === tab.id }" role="tab" :aria-selected="collection === tab.id" @click="collection = tab.id">{{ tab.label }}</button>
    </div>
    <div class="gm-toolbar">
      <input v-model="search" class="gm-search" type="search" placeholder="Search templates" aria-label="Search email templates" />
      <label v-if="collection === 'saved'" class="gm-check"><input v-model="archived" type="checkbox" />Show archived</label>
      <span class="gm-muted" aria-live="polite">{{ total }} templates</span>
    </div>
    <div v-if="error" class="gm-error" role="alert">{{ error }} <gs-button variant="outline" @click="load()">Try again</gs-button></div>
    <p v-if="loading" role="status">Loading templates…</p>
    <p v-if="choosing" role="status">Opening your template…</p>
    <div v-if="selectionError" class="gm-error" role="alert">{{ selectionError }}</div>
    <div v-if="!loading && !filtered.length && !error" class="gm-panel gm-empty">
      <h2>{{ search ? 'No matching templates' : 'No templates here yet' }}</h2>
      <p class="gm-muted">{{ search ? 'Try a different name or keyword.' : 'Choose a professional design and save a copy to make it your own.' }}</p>
    </div>
    <div class="gm-grid" :aria-busy="loading || !!choosing">
      <article v-for="template in filtered" :key="template.id" class="gm-card">
        <button class="gm-card-preview" :aria-label="'Preview ' + template.name" :disabled="!!choosing" @click="choose(template)">
          <iframe v-if="template.bodyHtml" :title="template.name + ' preview'" :srcdoc="preview(template.bodyHtml)" sandbox="" tabindex="-1" loading="lazy" />
          <img v-else-if="template.thumbnail" :src="template.thumbnail" :alt="template.name" loading="lazy" class="gm-template-thumbnail" />
          <span v-else class="gm-muted">{{ template.name }}</span>
        </button>
        <div class="gm-card-content">
          <small>{{ collection === 'professional' ? 'Professional design' : collection === 'emails' ? 'Saved email' : template.status === 'archived' ? 'Archived template' : 'Your template' }}</small>
          <h3>{{ template.name }}</h3>
          <p class="gm-muted">{{ template.subject || (template.tags || []).slice(0, 3).join(' · ') }}</p>
          <gs-button variant="outline" :disabled="!!choosing" @click="choose(template)">{{ choosing === template.id ? 'Opening…' : embedded ? 'Use template' : template.source === 'studio' ? 'Edit template' : 'Customize a copy' }}</gs-button>
        </div>
      </article>
    </div>
    <div v-if="lastPage > 1" class="gm-toolbar gm-library-pagination" aria-label="Template pages">
      <gs-button variant="outline" :disabled="page === 1 || loading" @click="page--">Previous</gs-button>
      <span>Page {{ page }} of {{ lastPage }}</span>
      <gs-button variant="outline" :disabled="page === lastPage || loading" @click="page++">Next</gs-button>
    </div>
  </section>
</template>
<script>
import axios from 'axios';
import templates from './data/email-templates.json';
import { clone, errorMessage, previewDocument } from './contracts';
import { fetchLibraryPage, resolveLibraryTemplate } from './template-library';
export default {
  props: { embedded: Boolean, initialCollection: { type: String, default: 'professional' } },
  data() {
    return {
      collection: ['professional', 'saved', 'emails'].includes(this.initialCollection) ? this.initialCollection : 'professional',
      collections: [{ id: 'professional', label: 'Professional templates' }, { id: 'saved', label: 'My templates' }, { id: 'emails', label: 'Saved emails' }],
      search: '', saved: [], remote: [], error: '', loading: false, archived: false,
      page: 1, lastPage: 1, remoteTotal: 0, requestId: 0, searchTimer: null,
      choosing: '', selectionError: ''
    };
  },
  computed: {
    localMatches() {
      return (this.collection === 'professional' ? templates : this.collection === 'saved' ? this.saved : [])
        .filter(item => (this.archived || item.status !== 'archived') &&
          (item.name + ' ' + (item.subject || '')).toLowerCase().includes(this.search.toLowerCase()));
    },
    total() { return this.remoteTotal + this.localMatches.length; },
    filtered() { return (this.page === 1 ? this.localMatches : []).concat(this.remote); }
  },
  watch: {
    initialCollection(value) { if (this.collections.some(tab => tab.id === value)) this.collection = value; },
    collection() { this.resetAndLoad(); },
    page() { this.load(); },
    search() {
      this.requestId++;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.resetAndLoad(), 300);
    }
  },
  created() { this.load(); },
  beforeDestroy() { clearTimeout(this.searchTimer); this.requestId++; },
  methods: {
    preview: previewDocument,
    resetAndLoad() {
      this.remote = []; this.remoteTotal = 0; this.lastPage = 1;
      if (this.page !== 1) this.page = 1;
      else this.load();
    },
    async choose(template) {
      if (this.choosing) return;
      this.choosing = template.id; this.selectionError = '';
      try { this.$emit('choose', clone(await resolveLibraryTemplate(template))); }
      catch (error) { this.selectionError = errorMessage(error); }
      finally { this.choosing = ''; }
    },
    async load() {
      const request = ++this.requestId;
      const collection = this.collection;
      this.loading = true; this.error = ''; this.remote = [];
      const results = await Promise.all([
        fetchLibraryPage(collection, this.page, this.search).then(value => ({ value })).catch(error => ({ error })),
        collection === 'saved' ? axios.get('/studio/email-templates', { timeout: 20000 }).then(response => ({ value: response.data.data })).catch(error => ({ error })) : Promise.resolve(null)
      ]);
      if (request !== this.requestId) return;
      if (results[0].value) {
        this.remote = results[0].value.items;
        this.remoteTotal = results[0].value.total;
        this.lastPage = results[0].value.lastPage;
      }
      if (results[1] && results[1].value) this.saved = results[1].value.map(item => ({ ...item, source: 'studio' }));
      this.error = results.filter(result => result && result.error).map(result => errorMessage(result.error)).join(' ');
      this.loading = false;
    }
  }
};
</script>
<style scoped>
.gm-template-thumbnail { width: 100%; height: 100%; object-fit: cover; object-position: top; }
.gm-library-pagination { justify-content: center; margin-top: 24px; }
</style>
